<template>
    <div
        v-if="!chat.loading.value"
        v-show="!chat.isQuickOptionHidden"
        :class="componentClasses"
    >
        <button
            v-for="(option, index) in quickOptions"
            :key="index"
            type="button"
            :class="quickOptionComponentClass"
            @click="quickOptionPressed(localizedQuickOptionMessage(option))"
        >
            {{ localizedQuickOptionShortMessage(option) }}
        </button>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { type QuickOption, VisitorInteractionType } from '@keyvue/one-api-client';
    import useConfiguration from '@/composables/useConfiguration';
    import { useI18n } from 'vue-i18n';
    import Chat from '@/chat/Chat';
    import useVisitorData from '@/composables/useVisitorData';

    defineProps<{
        quickOptions: QuickOption[],
    }>();

    const chat = Chat.getInstance();
    const { locale } = useI18n();
    const { assistent } = useConfiguration();
    const { registerInteraction } = useVisitorData();

    function localizedQuickOptionMessage(option: QuickOption): string {
        return assistent.value?.behavior.englishSupportEnabled
            ? option.getMessageForLocale(locale.value)
            : option.message;
    }

    function localizedQuickOptionShortMessage(option: QuickOption): string {
        return assistent.value?.behavior.englishSupportEnabled
            ? option.getShortMessageForLocale(locale.value)
            : option.shortMessage;
    }

    const componentClasses = computed(() => {
        return {
            'quick-options': true,
        };
    });

    const quickOptionComponentClass = computed(() => {
        return {
            'quick-options__button': true,
            'quick-options__button--disabled': chat.loading.value,
        };
    });

    async function quickOptionPressed(quickOptionMessage: string) {
        if (chat.loading.value) {
            return;
        }

        await chat.sendMessage(quickOptionMessage);
        await registerInteraction(VisitorInteractionType.QUICK_OPTION_CLICKED);
    }
</script>

<style scoped lang="scss">
    .quick-options {
        display: inline-flex;
        flex-wrap: wrap;
        margin: 0 10px;

        &__button {
            cursor: pointer;
            text-align: center;
            flex-grow: 1;
            font-size: .9rem;

            &:first-child, &:last-child {
                flex-grow: unset;
            }

            &--disabled {
                border: 1px solid gray;
                color: gray;
                cursor: not-allowed;
            }
        }
    }

    .app--flat {
        .quick-options {
            &__button {
                margin: 5px 5px;
                background-color: white;
                padding: 4px 6px;
                color: var(--color-widget);
                border: 1px solid var(--color-widget);
                border-radius: 8px;
            }
        }
    }

    .app--modern {
        .quick-options {
            justify-content: flex-end;

            &__button {
                background-color: var(--color-balloon);
                color: var(--color-widget-text);
                margin: 12px 5px;
                padding: 12px 16px;
                border: 0;
                border-radius: 8px;
            }
        }
    }
</style>
